import { useEffect, useState } from 'react';
import { Box, Container, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import nodownload from '../../assets/no.png';
import slide1 from '../../assets/slide1.png';
import slide2 from '../../assets/slide2.png';
import slide3 from '../../assets/slide3.png';
import slide4 from '../../assets/slide4.png';
import slide5 from '../../assets/slide0.png';

const AppInterface = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentSlide, setCurrentSlide] = useState(0);

  const slideImages = [slide1, slide2, slide3, slide4, slide5];
  const totalSlides = slideImages.length;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const prevIndex = (currentSlide - 1 + totalSlides) % totalSlides;
  const nextIndex = (currentSlide + 1) % totalSlides;

  return (
    <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 'bold',
              mb: 3,
              fontSize: { xs: '1.8rem', md: '2.5rem' },
              color: '#333',
              letterSpacing: '0.5px',
            }}
          >
            CHECKOUT OUR APP INTERFACE LOOK
          </Typography>
          <Typography
            variant="body1"
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              color: '#666',
              lineHeight: 1.6,
              fontSize: { xs: '0.9rem', md: '1rem' },
            }}
          >
            Experience a sleek, modern, and user-friendly interface designed to make every
            interaction smooth and effortless. Our app combines powerful features with an intuitive
            design, helping users navigate easily and enjoy a seamless digital experience. From
            clean layouts to fast performance, every screen is crafted to deliver simplicity,
            efficiency, and a premium feel. Whether you're exploring features, managing tasks, or
            connecting with services, the interface is built to keep everything accessible,
            engaging, and visually appealing.
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          {/* Left Arrow */}
          <IconButton
            onClick={prevSlide}
            sx={{
              position: 'absolute',
              left: { xs: 10, md: 50 },
              zIndex: 2,
              backgroundColor: 'white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <ArrowBackIos sx={{ color: '#666' }} />
          </IconButton>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, md: 4 },
              px: { xs: 4, md: 12 },
              minHeight: '500px',
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                opacity: 0.5,
                transform: 'scale(0.85)',
                transition: 'transform 0.5s ease',
              }}
            >
              <img
                src={slideImages[prevIndex]}
                alt="Previous preview"
                style={{
                  width: '200px',
                  height: '400px',
                  objectFit: 'cover',
                }}
              />
            </Box>

            {/* Center Image */}
            <Box>
              <img
                src={slideImages[currentSlide]}
                alt="Main slide"
                style={{
                  width: isMobile ? '250px' : '280px',
                  height: isMobile ? '500px' : '560px',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                }}
              />
            </Box>

            {/* Right Preview */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                opacity: 0.5,
                transform: 'scale(0.85)',
                transition: 'transform 0.5s ease',
              }}
            >
              <img
                src={slideImages[nextIndex]}
                alt="Next preview"
                style={{
                  width: '200px',
                  height: '400px',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </Box>

          {/* Right Arrow */}
          <IconButton
            onClick={nextSlide}
            sx={{
              position: 'absolute',
              right: { xs: 10, md: 50 },
              zIndex: 2,
              backgroundColor: 'white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <ArrowForwardIos sx={{ color: '#666' }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 8 }}>
          {slideImages.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentSlide(index)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: index === currentSlide ? '#6366f1' : '#d1d5db',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: index === currentSlide ? '#5855eb' : '#9ca3af',
                },
              }}
            />
          ))}
        </Box>
      </Container>

      <Box
        sx={{
          maxWidth: '100%',
          px: { xs: 2, md: 6 },
          mx: { xs: 0, md: 6 },
          py: 8,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              pr: { md: 4 },
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              variant="h4"
              component="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3,
                fontSize: { xs: '1.5rem', md: '2rem' },
                color: '#333',
              }}
            >
              NO DOWNLOAD NECESSARY
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                lineHeight: 1.6,
                mb: 4,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              Homestay is fully web-based, so guests don't need to download an app in order to use
              it. They can use it straight from their phone just like an extension of your website.
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: '#4285f4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.2rem' }}>
                  G
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: '#007aff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.2rem' }}>
                  S
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' },
              mt: { xs: 4, md: 0 },
            }}
          >
            <img
              src={nodownload}
              alt="Web-based mobile interface"
              style={{
                width: '100%',
                maxWidth: '300px',
                height: 'auto',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppInterface;
